import { useState, Dispatch, SetStateAction } from 'react';
import LocalStorage from 'services/local-storage';

// Hook
export function useLocalStorage<S>(
    key: string,
    initialValue: S | (() => S)
): [S, Dispatch<SetStateAction<S>>] {
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState<S>(() => {
        try {
            // Get from local storage by key
            return LocalStorage.getItem(key, initialValue);
        } catch (error) {
            // If error also return initialValue
            console.log(error);
            return initialValue;
        }
    });

    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue = (value: S | ((prevState: S) => S)) => {
        try {
            // Allow value to be a function so we have same API as useState
            const valueToStore: S =
                value instanceof Function ? value(storedValue) : value;
            // Save state
            setStoredValue(valueToStore);
            // Save to local storage
            LocalStorage.setItem(key, valueToStore);
        } catch (error) {
            // A more advanced implementation would handle the error case
            console.log(error);
        }
    };

    return [storedValue, setValue];
}
